import React, { useState, useEffect, useCallback } from 'react';
import { getData, patchData, postData } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { getPushNotificationStatus, registerPushNotifications, isLikelyWebView } from '../../../utils/pushNotifications';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import EmptyState from '../../../components/common/EmptyState';
import AlertToast from '../../../components/common/AlertToast';
import Pagination from '../../../components/common/Pagination';

const TYPE_ICONS = {
  info: { icon: 'bi-info-circle-fill', color: 'text-primary' },
  success: { icon: 'bi-check-circle-fill', color: 'text-success' },
  warning: { icon: 'bi-exclamation-triangle-fill', color: 'text-warning' },
  error: { icon: 'bi-x-circle-fill', color: 'text-danger' },
  reminder: { icon: 'bi-bell-fill', color: 'text-info' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN');
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const role = user?.roleKey || user?.role || '';
  const canTest = ['admin','manager'].includes(role) || user?.userType === 'super_admin';
  const inWebView = isLikelyWebView();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | read
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });
  const [toast, setToast] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);
  const [testing, setTesting] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [pushStatus, setPushStatus] = useState({ supported: true, permission: 'default', subscribed: false });
  const [enablingPush, setEnablingPush] = useState(false);
  const [pushServerStatus, setPushServerStatus] = useState({ configured: false, subscriptionCount: 0, subscriptions: [] });

  const refreshPushStatus = useCallback(async () => {
    const status = await getPushNotificationStatus();
    setPushStatus(status);
  }, []);

  const refreshPushServerStatus = useCallback(async () => {
    if (inWebView) {
      setPushServerStatus({ configured: false, subscriptionCount: 0, subscriptions: [] });
      return;
    }
    try {
      const res = await getData('/shared/notifications/push-status');
      const data = res.data || res;
      setPushServerStatus({
        configured: !!data.configured,
        subscriptionCount: data.subscriptionCount || 0,
        subscriptions: Array.isArray(data.subscriptions) ? data.subscriptions : [],
      });
    } catch (_) {
      setPushServerStatus({ configured: false, subscriptionCount: 0, subscriptions: [] });
    }
  }, [inWebView]);

  const sendTestNotification = async () => {
    setTesting(true);
    try {
      const r = await postData('/shared/notifications/test');
      setDiagnostics(r.data?.diagnostics || r.diagnostics || null);
      setToast({ type: 'success', message: 'Test notification sent — should appear in this list (and your bell icon should tick +1).' });
      // Reload after a beat so the new row shows.
      setTimeout(() => fetchNotifications(1), 300);
    } catch (e) {
      setToast({ type: 'danger', message: e.response?.data?.message || e.message || 'Test failed.' });
    }
    setTesting(false);
  };

  const enableBrowserNotifications = async () => {
    if (inWebView) {
      setToast({
        type: 'info',
        message: 'Browser push is skipped inside the mobile app WebView. This app uses native Firebase notifications instead.',
      });
      return;
    }
    setEnablingPush(true);
    try {
      const ok = await registerPushNotifications({ requestPermission: true });
      await refreshPushStatus();
      await refreshPushServerStatus();
      if (ok) {
        await postData('/shared/notifications/push-test').catch(() => {});
      }
      setToast({
        type: ok ? 'success' : 'warning',
        message: ok
          ? 'Browser notifications enabled. A push test has been sent to this device.'
          : 'Browser notifications are still not active. Check browser permission and device support.',
      });
    } catch (e) {
      setToast({ type: 'danger', message: e.response?.data?.message || e.message || 'Could not enable browser notifications.' });
    }
    setEnablingPush(false);
  };

  const fetchNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter === 'unread') params.isRead = false;
      if (filter === 'read') params.isRead = true;
      const res = await getData('/shared/notifications', params);
      // Backend now returns paginated(): { success, message, records, total, page, limit, totalPages }
      const list = res.records || res.data?.notifications || res.notifications || [];
      setNotifications(list);
      if (res.totalPages !== undefined) {
        setPagination({
          page: res.page || page,
          totalPages: res.totalPages,
          total: res.total || list.length,
          limit: res.limit || 20,
        });
      }
    } catch (err) {
      setToast({ type: 'danger', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchNotifications(1); }, [fetchNotifications]);
  useEffect(() => { refreshPushStatus(); }, [refreshPushStatus]);
  useEffect(() => { refreshPushServerStatus(); }, [refreshPushServerStatus]);

  const handleMarkRead = async (id) => {
    try {
      await patchData(`/shared/notifications/${id}/read`);
      // On the Unread/Read tab the row's filter status has changed — refetch so it
      // disappears from the wrong tab. On All, optimistic update is enough.
      if (filter !== 'all') {
        fetchNotifications(pagination.page);
      } else {
        setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true, readAt: new Date() } : n));
      }
    } catch (err) {
      setToast({ type: 'danger', message: err.message });
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await patchData('/shared/notifications/read-all');
      setToast({ type: 'success', message: 'All notifications marked as read.' });
      // Always refetch — Unread should empty out, Read should fill up.
      fetchNotifications(1);
    } catch (err) {
      setToast({ type: 'danger', message: err.message });
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div>
      {toast && <AlertToast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <PageHeader
        title="Notifications"
        icon="bi bi-bell"
        subtitle={unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up'}
        actions={(
          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm ${pushStatus.subscribed ? 'btn-outline-success' : 'btn-outline-primary'}`}
              onClick={enableBrowserNotifications}
              disabled={inWebView || enablingPush || pushStatus.permission === 'denied' || pushStatus.supported === false}
              title="Enable browser and OS notifications for this device."
            >
              {enablingPush
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Enabling...</>
                : <><i className={`bi ${pushStatus.subscribed ? 'bi-bell-fill' : 'bi-bell'} me-1`}></i>{pushStatus.subscribed ? 'Notifications Enabled' : 'Enable Browser Notifications'}</>}
            </button>
            {canTest && (
              <button className="btn btn-outline-secondary btn-sm" onClick={sendTestNotification} disabled={testing} title="Send a test notification to yourself to verify the pipeline works.">
                {testing
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Sending...</>
                  : <><i className="bi bi-bell-fill me-1"></i>Send Test Notification</>}
              </button>
            )}
            {unreadCount > 0 && (
              <button className="btn btn-outline-primary btn-sm" onClick={handleMarkAllRead} disabled={markingAll}>
                {markingAll
                  ? <><span className="spinner-border spinner-border-sm me-1"></span>Marking...</>
                  : <><i className="bi bi-check-all me-1"></i>Mark All Read</>}
              </button>
            )}
          </div>
        )}
      />

      <div className={`alert py-2 small ${pushStatus.subscribed ? 'alert-success' : 'alert-warning'}`}>
        <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
          <div>
            <strong><i className="bi bi-app-indicator me-1"></i>Browser notification status</strong>
            {inWebView && (
              <div className="mt-1 text-info">
                This page is running inside the mobile app WebView. Browser push is disabled here; delivery should come from native Firebase notifications registered by the mobile app.
              </div>
            )}
            <div className="mt-1">
              Permission: <strong>{pushStatus.permission}</strong> · Subscription: <strong>{pushStatus.subscribed ? 'active' : 'inactive'}</strong>
            </div>
            {!inWebView && (
              <div className="mt-1">
                Server push: <strong>{pushServerStatus.configured ? 'configured' : 'not configured'}</strong> | Saved devices: <strong>{pushServerStatus.subscriptionCount}</strong>
              </div>
            )}
            {!pushStatus.supported && (
              <div className="mt-1 text-danger">
                {inWebView
                  ? 'Android or iOS WebView does not support browser background push in the same way as Chrome or Safari. Native mobile push is used instead.'
                  : 'This browser or device does not support background push notifications.'}
              </div>
            )}
            {pushStatus.permission === 'denied' && (
              <div className="mt-1 text-danger">Notifications are blocked in browser settings for this site.</div>
            )}
            {pushStatus.supported && !inWebView && !pushServerStatus.configured && (
              <div className="mt-1 text-danger">Server VAPID keys are missing, so closed-app push delivery cannot work yet.</div>
            )}
            {pushStatus.supported && !inWebView && pushStatus.permission !== 'denied' && !pushStatus.subscribed && (
              <div className="mt-1">Click <strong>Enable Browser Notifications</strong> once on this device to receive notifications when the app is closed.</div>
            )}
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { refreshPushStatus(); refreshPushServerStatus(); }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh Status
          </button>
        </div>
      </div>

      {diagnostics && (
        <div className="alert alert-info py-2 small">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <strong><i className="bi bi-info-circle me-1"/>Notification pipeline diagnostics</strong>
            <button className="btn-close btn-sm" onClick={() => setDiagnostics(null)}/>
          </div>
          <div>
            Your user ID: <code>{diagnostics.yourUserId}</code> · Tenant: <code>{diagnostics.yourTenantId}</code> · Role: <strong>{diagnostics.yourRoleKey}</strong>
          </div>
          <div>
            <strong>Tenant total notifications:</strong> {diagnostics.tenantTotal} ·
            <strong> Yours:</strong> {diagnostics.yourTotal} ({diagnostics.yourUnread} unread)
          </div>
          {diagnostics.tenantTotal === 0 && (
            <div className="text-danger mt-1">
              ⚠ The tenant has zero notifications. Trigger an event (e.g., create an enquiry, post an announcement, submit a daily report) and check again.
            </div>
          )}
          {diagnostics.tenantTotal > 0 && diagnostics.yourTotal === 0 && (
            <div className="text-warning mt-1">
              ⚠ The tenant has {diagnostics.tenantTotal} notifications but none for you. Most events notify <em>other</em> users (admins/parents/etc.) — log in as a different user, or have someone else perform an action.
            </div>
          )}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="d-flex gap-2">
            {['all', 'unread', 'read'].map(f => (
              <button
                key={f}
                className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setFilter(f)}
              >
                {f === 'all' && <><i className="bi bi-list me-1"></i>All</>}
                {f === 'unread' && <><i className="bi bi-envelope me-1"></i>Unread</>}
                {f === 'read' && <><i className="bi bi-envelope-open me-1"></i>Read</>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      {loading ? <LoadingSpinner /> : notifications.length === 0 ? (
        <EmptyState
          icon="bi bi-bell-slash"
          title="No notifications"
          message={filter === 'unread' ? 'You have no unread notifications.' : 'No notifications found.'}
        />
      ) : (
        <>
          <div className="card">
            <div className="list-group list-group-flush">
              {notifications.map((n, idx) => {
                const typeInfo = TYPE_ICONS[n.type] || TYPE_ICONS.info;
                return (
                  <div
                    key={n._id}
                    className={`list-group-item list-group-item-action d-flex gap-3 py-3 ${!n.isRead ? 'bg-light' : ''}`}
                    style={{ borderLeft: !n.isRead ? '4px solid var(--tenant-primary, #0d6efd)' : '4px solid transparent' }}
                  >
                    {/* Icon */}
                    <div className="flex-shrink-0 pt-1">
                      <i className={`bi ${typeInfo.icon} fs-5 ${typeInfo.color}`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <p className={`mb-1 ${!n.isRead ? 'fw-semibold' : ''}`} style={{ fontSize: '0.9rem' }}>
                            {n.title || n.message}
                          </p>
                          {n.title && n.message && (
                            <p className="mb-0 text-muted" style={{ fontSize: '0.82rem' }}>{n.message}</p>
                          )}
                          {n.module && (
                            <span className="badge bg-secondary bg-opacity-10 text-secondary mt-1" style={{ fontSize: '0.75rem' }}>
                              {n.module}
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-column align-items-end gap-1 ms-2 flex-shrink-0">
                          <small className="text-muted">{timeAgo(n.createdAt)}</small>
                          {!n.isRead && (
                            <button
                              className="btn btn-link btn-sm p-0 text-primary"
                              style={{ fontSize: '0.78rem' }}
                              onClick={() => handleMarkRead(n._id)}
                            >
                              Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={fetchNotifications}
          />
        </>
      )}
    </div>
  );
}
